/**
 * The block popup (v0.9.0's P2a; ADR 0184): a block's details over the dimmed
 * board, where the day view's right-hand panel used to be. The owner: "When a
 * block is selected a popup happened instead of the right side permanently
 * being the way we interact with blocks." What's in it is BlockDetails: the
 * block on the left, what to do with it on the right, its other actions
 * behind a More row (ADR 0193).
 *
 * - **A dialog** (Radix, as Owner tools is): named by the block's heading,
 *   which takes focus as it opens. Focus stays inside while it's open, and
 *   goes back to the block's card when it closes. The board behind doesn't
 *   scroll meanwhile (DialogBackdrop; ADR 0191).
 * - **Its bar:** Previous block and Next block, "Block 2 of 7 · 9:05–9:40 AM",
 *   and Close. ← and → step through the day's blocks in time order, never
 *   while typing or in a list; Escape, Close or a click outside it closes it.
 * - **Up to 960 px wide,** with two panes side by side and no scroll bar for
 *   a typical block at 1440 × 900 (ADR 0193); the panes stack below 860 px,
 *   and below 640 px it's a full-screen sheet.
 * - **The calls stay usable,** and what's done is still said (DayPage's live
 *   parts): while it's open, the ringing and live call cards and the day's
 *   status region are moved into it, so they're above its backdrop, inside
 *   its focus, still answer a click, and never hidden from a screen reader.
 */
import * as Dialog from "@radix-ui/react-dialog";
import { useCallback, useEffectEvent, useLayoutEffect, useRef, useState } from "react";

import type { DismissReason } from "../../api/types";
import { DialogBackdrop } from "../../components/ui/DialogBackdrop";
import { isMenuDismissPress } from "../../components/ui/useMenu";
import { IconButton } from "../../components/ui/IconButton";
import { ChevronLeftIcon, ChevronRightIcon } from "../../components/ui/Icons";
import { formatClockRange } from "../../lib/dates";
import { isOpenList, takesArrowKeys } from "../../lib/keys";
import { BlockDetails, type OpenOptions, type PopupStart } from "./BlockDetails";
import type { TicketCardView } from "./board";
import type { LogStart } from "./LogToAutotask";
import { type BlockView, startsOwnFocus } from "./model";
import { SentHere, SentHereContext } from "./sentHere";

/** Moves the day's live parts into the open popup, or back with null (DayPage). */
export type AdoptLiveParts = (into: HTMLElement | null) => void;

// Where Space is already the focus's own (ADR 0194 decision 4): it types in a
// field, presses a button or a box, picks in a list or a menu, or scrolls the
// activities' own box. Anywhere else in the popup, it closes it.
const SPACE_IS_ITS_OWN =
  'input, textarea, select, button, a[href], summary, [contenteditable]:not([contenteditable="false"]), ' +
  '[role="button"], [role="checkbox"], [role="radio"], [role="switch"], [role="option"], [role="menuitem"], ' +
  '[role="combobox"], [role="listbox"], [role="menu"], [data-part="activity-list"]';

const stepButton = "shrink-0 disabled:cursor-default disabled:opacity-40 disabled:hover:bg-panel";

export function BlockPopup({
  block,
  order,
  timeZone,
  targets,
  onStep,
  onSelect,
  onClose,
  onClosed,
  onLogged,
  onDismissed,
  adoptLiveParts,
  logStart,
  start,
  onOpenBlock,
  ticketCard,
}: {
  /** The block it shows; null, and it's closed. */
  block: BlockView | null;
  /** Its ticket's card, when its ticket has other time entries that day: listed in it (ADR 0189). */
  ticketCard?: TicketCardView | null | undefined;
  /** A block dropped on an open ticket: its Log form starts there (v0.9.0's P4). */
  logStart?: LogStart | null | undefined;
  /**
   * Where it starts, for its block (ADR 0194): on a pane, or with the Log
   * form's Ticket list open. `serial` tells a new request on the same block.
   */
  start?: (PopupStart & { serial: number }) | null | undefined;
  /** Shows another block, on a pane or its Log form on a ticket (the menus' "Log a Ready block here"). */
  onOpenBlock?: ((blockId: string, options: OpenOptions) => void) | undefined;
  /** The day's blocks on the board, in time order: what Previous and Next step through. */
  order: readonly BlockView[];
  timeZone: string;
  /** The day's other blocks an activity can move to (ADR 0060). */
  targets: readonly BlockView[];
  /** Shows the block before (-1) or after (1) this one. */
  onStep: (by: -1 | 1) => void;
  /** Shows another block, as a merge does (#248). */
  onSelect: (blockId: string) => void;
  /** Escape, Close, or a click outside. */
  onClose: () => void;
  /** Once it's gone: where focus goes back to (the day view decides). */
  onClosed: () => void;
  /** Logged from here: the day view closes it (ADR 0184). */
  onLogged: (ticket: string | null) => void;
  /** Dismissed from its Dismiss ▾: the board offers Undo (Discussion #877). */
  onDismissed?: ((block: BlockView, reason: DismissReason) => void) | undefined;
  adoptLiveParts?: AdoptLiveParts | undefined;
}) {
  const heading = useRef<HTMLHeadingElement>(null);
  // What was sent from the popup, kept while it's closed or shows another
  // block: logged, it still closes the popup (ADR 0184). The popup is on the
  // day view as long as the day is shown.
  const [sentHere] = useState(() => new SentHere());
  // Stepping to another block, or a merge, moves focus to its heading as
  // opening does (Idea #287). The first opening is onOpenAutoFocus's: the
  // dialog isn't on the page yet here.
  const blockId = block?.id ?? null;
  // Started on a pane, the Ticket list, a form's field or Close ticket's
  // question: what it opens takes focus instead.
  const startHere = start !== null && start !== undefined && start.blockId === blockId ? start : null;
  const ownFocus = startHere !== null && startsOwnFocus(startHere);
  const startSerial = startHere?.serial ?? null;
  const focusHeading = useEffectEvent(() => {
    if (!ownFocus) {
      heading.current?.focus();
    }
  });
  // Once per block, or per request to start it somewhere.
  useLayoutEffect(() => {
    if (blockId !== null) {
      focusHeading();
    }
  }, [blockId, startSerial]);
  // The live parts come in with the popup, and go back with it.
  const slot = useCallback(
    (element: HTMLDivElement | null) => {
      adoptLiveParts?.(element);
    },
    [adoptLiveParts],
  );
  const index = block === null ? -1 : order.findIndex((b) => b.id === block.id);

  return (
    <Dialog.Root
      open={block !== null}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <Dialog.Portal>
        {/* The backdrop holds the popup, so a long one scrolls inside the
            window, and a click on it, outside the popup, closes it. Ours,
            not Radix's Overlay, whose scroll lock the CSP refuses (ADR 0191). */}
        <DialogBackdrop
          data-testid="block-popup-backdrop"
          className="fixed inset-0 z-30 flex items-center justify-center bg-black/45 p-6 motion-safe:animate-fade-in max-[639px]:p-0 dark:bg-black/60"
        >
          {block !== null ? (
            <Dialog.Content
              // Radix hides the rest of the page from a screen reader; this
              // says so to one that goes by the attribute.
              aria-modal="true"
              aria-describedby={undefined}
              data-part="block-popup"
              className={
                "flex max-h-full w-full max-w-[960px] flex-col overflow-hidden rounded-panel border border-line " +
                "bg-panel text-text shadow-[0_24px_60px_rgba(0,0,0,0.16)] motion-safe:animate-fade-in " +
                // A phone: the whole screen, a sheet.
                "max-[639px]:h-full max-[639px]:max-w-none max-[639px]:rounded-none max-[639px]:border-0"
              }
              onOpenAutoFocus={(event) => {
                event.preventDefault();
                // Unless what it started on has taken focus already.
                if (!(event.currentTarget instanceof Element && event.currentTarget.contains(document.activeElement))) {
                  heading.current?.focus();
                }
              }}
              // A press outside that closed one of its menus closes only the
              // menu, as in the prototype (ADR 0194).
              onPointerDownOutside={(event) => {
                if (isMenuDismissPress(event.detail.originalEvent)) {
                  event.preventDefault();
                }
              }}
              onCloseAutoFocus={(event) => {
                event.preventDefault();
                onClosed();
              }}
              onEscapeKeyDown={(event) => {
                if (isOpenList(event.target)) {
                  // The list closes itself; the popup stays.
                  event.preventDefault();
                }
              }}
              onKeyDown={(event) => {
                // Space closes it, as it opened it, unless it's for what has
                // the focus (ADR 0194 decision 4).
                if (
                  event.key === " " &&
                  !event.repeat &&
                  !event.defaultPrevented &&
                  !event.altKey &&
                  !event.ctrlKey &&
                  !event.metaKey &&
                  !event.shiftKey &&
                  !(event.target instanceof Element && event.target.closest(SPACE_IS_ITS_OWN))
                ) {
                  event.preventDefault();
                  event.stopPropagation();
                  onClose();
                  return;
                }
                if (
                  (event.key !== "ArrowLeft" && event.key !== "ArrowRight") ||
                  event.altKey ||
                  event.ctrlKey ||
                  event.metaKey ||
                  event.shiftKey ||
                  event.defaultPrevented ||
                  takesArrowKeys(event.target)
                ) {
                  return;
                }
                const by = event.key === "ArrowLeft" ? -1 : 1;
                if (order[index + by] !== undefined) {
                  event.preventDefault();
                  onStep(by);
                }
              }}
            >
              <PopupBar
                index={index}
                total={order.length}
                range={formatClockRange(block.startedAt, block.endedAt, timeZone)}
                onStep={onStep}
                onClose={onClose}
              />
              <SentHereContext.Provider value={sentHere}>
                <BlockDetails
                  // Afresh for each block, and each request to start it somewhere.
                  key={startSerial === null ? block.id : `${block.id}:${String(startSerial)}`}
                  block={block}
                  timeZone={timeZone}
                  targets={targets}
                  onSelect={onSelect}
                  onOpenBlock={onOpenBlock}
                  onClose={onClose}
                  headingRef={heading}
                  onLogged={onLogged}
                  onDismissed={onDismissed}
                  logStart={logStart}
                  start={startHere}
                  ticketCard={ticketCard}
                />
              </SentHereContext.Provider>
              {/* The live parts, while the popup is open (DayPage). */}
              {adoptLiveParts ? <div ref={slot} className="contents" /> : null}
            </Dialog.Content>
          ) : null}
        </DialogBackdrop>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** Previous and Next, where this block is in the day, and Close. */
function PopupBar({
  index,
  total,
  range,
  onStep,
  onClose,
}: {
  index: number;
  total: number;
  range: string;
  onStep: (by: -1 | 1) => void;
  onClose: () => void;
}) {
  return (
    <div className="flex shrink-0 items-center gap-2 border-b border-line py-2.5 pr-3 pl-3.5">
      <IconButton
        aria-label="Previous block"
        title="Previous block (←)"
        disabled={index <= 0}
        onClick={() => {
          onStep(-1);
        }}
        className={stepButton}
      >
        <ChevronLeftIcon />
      </IconButton>
      <IconButton
        aria-label="Next block"
        title="Next block (→)"
        disabled={index < 0 || index >= total - 1}
        onClick={() => {
          onStep(1);
        }}
        className={stepButton}
      >
        <ChevronRightIcon />
      </IconButton>
      <p className="min-w-0 truncate pl-1 text-[12.5px] text-muted">
        {index >= 0 ? `Block ${String(index + 1)} of ${String(total)} · ` : null}
        <span className="font-mono text-xs">{range}</span>
      </p>
      <IconButton aria-label="Close" title="Close (Esc)" onClick={onClose} className="ml-auto shrink-0 text-lg leading-none">
        <span aria-hidden="true">×</span>
      </IconButton>
    </div>
  );
}
